import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/lib/supabase/client';
import { useBusinessConfig } from '@/hooks/useBusinessConfig';
import { getTemplate } from '@/lib/vbs/templates/businessTemplates';
import { Plus, Trash2, Zap, Save, Loader2, AlertTriangle, Package } from 'lucide-react';
import { toast } from 'sonner';
import { calculateQuoteShortfalls } from '@/lib/vbs/utils/inventory-logic';

interface Service {
  id: string;
  name: string;
  description: string;
  base_price: number;
  duration_minutes: number;
}

interface InventoryItem {
  id: string;
  item_name: string;
  current_stock: number;
  unit: string;
}

interface ServiceInventoryRequirement {
  inventory_item_id: string;
  quantity_required: number;
  notes: string;
  item_name: string;
  unit: string;
}

interface LineItem {
  description: string;
  unit: string;
  quantity: number;
  price: number;
  service_id?: string;
  inventory_item_id?: string;
  quantity_required?: number;
  service_requirements?: ServiceInventoryRequirement[];
}

interface Shortfall {
  item_name: string;
  required: number;
  available: number;
  deficit: number;
  unit: string;
}

export const QuotingEngine = () => {
  const { config } = useBusinessConfig();
  const template = getTemplate(config?.biz_type ?? 'handyman');
  const [userId, setUserId] = useState<string | null>(null);

  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Inventory awareness state
  const [services, setServices] = useState<Service[]>([]);
  const [_inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [shortfalls, setShortfalls] = useState<Shortfall[]>([]);
  const [_showServiceSelector, setShowServiceSelector] = useState<number | null>(null);

  // Get authenticated user
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) setUserId(user.id);
    };
    getUser();
  }, []);

  // Fetch services and inventory on mount
  useEffect(() => {
    const fetchData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch active services
      const { data: servicesData } = await supabase
        .from('services')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (servicesData) setServices(servicesData);

      // Fetch inventory items
      const { data: inventoryData } = await supabase
        .from('inventory')
        .select('id, item_name, current_stock, unit')
        .eq('user_id', user.id)
        .order('item_name', { ascending: true });

      if (inventoryData) setInventoryItems(inventoryData);
    };

    fetchData();
  }, []);

  // Calculate shortfalls whenever line items change
  useEffect(() => {
    const checkShortfalls = async () => {
      const itemsWithInventory = lineItems.filter(
        item => item.inventory_item_id && item.quantity_required
      );

      if (itemsWithInventory.length === 0) {
        setShortfalls([]);
        return;
      }

      const shortfallsList = await calculateQuoteShortfalls(
        lineItems.map(item => ({
          inventory_item_id: item.inventory_item_id,
          quantity_required: item.quantity_required,
          quantity: item.quantity,
        }))
      );

      setShortfalls(shortfallsList);
    };

    checkShortfalls();
  }, [lineItems]);

  const addBlankLine = () => {
    setLineItems([...lineItems, { description: '', unit: 'job', quantity: 1, price: 0 }]);
  };

  const addPreset = (preset: typeof template.quoteLineItemPresets[0]) => {
    setLineItems([...lineItems, {
      description: preset.description,
      unit: preset.unit,
      quantity: preset.defaultQty,
      price: preset.defaultPrice,
    }]);
  };

  const addService = (service: Service) => {
    // Fetch service inventory requirements
    const fetchRequirements = async () => {
      const { data: reqs } = await supabase
        .from('service_inventory_requirements')
        .select(`
          inventory_item_id,
          quantity_required,
          notes,
          inventory:inventory_items (
            item_name,
            unit
          )
        `)
        .eq('service_id', service.id);

      const requirements: ServiceInventoryRequirement[] = (reqs || []).map((r: any) => ({
        inventory_item_id: r.inventory_item_id,
        quantity_required: r.quantity_required,
        notes: r.notes || '',
        item_name: r.inventory?.item_name || 'Unknown',
        unit: r.inventory?.unit || 'units',
      }));

      setLineItems([...lineItems, {
        description: service.name,
        unit: 'service',
        quantity: 1,
        price: service.base_price,
        service_id: service.id,
        inventory_item_id: requirements.length > 0 ? requirements[0].inventory_item_id : undefined,
        quantity_required: requirements.length > 0 ? requirements[0].quantity_required : undefined,
        service_requirements: requirements,
      }]);

      setShowServiceSelector(null);
    };

    fetchRequirements();
  };

  const updateLine = (index: number, field: keyof LineItem, value: string | number | undefined) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    setLineItems(updated);
  };

  const removeLine = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const subtotal = lineItems.reduce((t, i) => t + i.quantity * i.price, 0);
  const gst = config?.gst_registered ? subtotal * 0.1 : 0;
  const total = subtotal + gst;

  const saveQuote = async () => {
    if (!clientName) { toast.error('Enter a client name first'); return; }
    if (lineItems.length === 0) { toast.error('Add at least one line item'); return; }
    if (!userId) { toast.error('You must be logged in to create quotes'); return; }
    setLoading(true);

    try {
      // First, insert the quote
      const { data: quoteData, error: quoteError } = await supabase
        .from('quotes')
        .insert([{
          user_id: userId,
          customer_name: clientName,
          customer_email: clientEmail || null,
          customer_phone: clientPhone || null,
          total_amount: total,
          status: 'pending',
          notes: notes || null,
          approved: false,
          is_interactive: false,
        }])
        .select()
        .single();

      if (quoteError) {
        console.error('Quote insert error:', quoteError);
        throw quoteError;
      }
      if (!quoteData) throw new Error('No quote data returned');

      // Then, insert line items with FK references
      const lineItemsData = lineItems.map(item => ({
        quote_id: quoteData.id,
        service_name: item.description || 'Service',
        description: item.description,
        quantity: item.quantity,
        price: item.price,
        service_id: item.service_id || null,
        inventory_item_id: item.inventory_item_id || null,
        quantity_required: item.quantity_required || null,
      }));

      const { error: itemsError } = await supabase
        .from('quote_items')
        .insert(lineItemsData);

      if (itemsError) {
        console.error('Quote items insert error:', itemsError);
        throw itemsError;
      }

      // Show shortfall warnings if any
      if (shortfalls.length > 0) {
        toast.warning('Quote saved with inventory warnings. Check stock levels before job.');
        shortfalls.forEach(s => {
          toast.warning(`⚠️ ${s.item_name}: Need ${s.required} ${s.unit}, have ${s.available}`);
        });
      } else {
        toast.success(`Quote saved for ${clientName}`);
      }

      setClientName('');
      setClientEmail('');
      setClientPhone('');
      setLineItems([]);
      setNotes('');
    } catch (error: any) {
      console.error('Quote save error:', error);
      toast.error('Failed to save quote: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 pb-10 max-w-2xl mx-auto">

      {/* Client Details */}
      <Card className="border-2 border-slate-100 shadow-none rounded-[2rem]">
        <CardHeader className="pb-2">
          <CardTitle className="font-black uppercase tracking-tighter text-slate-900 text-lg">Client Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input placeholder="Client / Customer Name *" value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            className="rounded-xl h-12 font-bold border-slate-100" />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Email Address" type="email" value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              className="rounded-xl h-12 font-bold border-slate-100" />
            <Input placeholder="Phone Number" type="tel" value={clientPhone}
              onChange={(e) => setClientPhone(e.target.value)}
              className="rounded-xl h-12 font-bold border-slate-100" />
          </div>
        </CardContent>
      </Card>

      {/* Quick-Add Presets */}
      <div>
        <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-3 ml-1">
          Quick-Add Services
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          {template.quoteLineItemPresets.map((preset) => (
            <button
              key={preset.description}
              onClick={() => addPreset(preset)}
              className="flex items-center gap-1.5 bg-white border-2 border-slate-100 hover:border-purple-300 hover:bg-purple-50 text-slate-700 font-bold text-xs px-3 py-2 rounded-xl transition-all"
            >
              <Zap className="w-3 h-3 text-purple-500" />
              {preset.description}
              {preset.defaultPrice > 0 && (
                <span className="text-slate-400 font-normal">
                  · ${preset.defaultPrice}{preset.unit !== 'job' ? `/${preset.unit}` : ''}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Services Dropdown */}
        {services.length > 0 && (
          <div className="flex items-center gap-2">
            <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">
              Or Add From Services
            </p>
            <select
              onChange={(e) => {
                const service = services.find(s => s.id === e.target.value);
                if (service) addService(service);
              }}
              value=""
              className="flex-1 h-10 rounded-xl border border-slate-200 text-sm font-bold px-3"
            >
              <option value="">-- Select a service --</option>
              {services.map(service => (
                <option key={service.id} value={service.id}>
                  {service.name} - ${service.base_price.toFixed(2)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Inventory Shortfall Warning */}
      {shortfalls.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 space-y-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <p className="text-sm font-black text-amber-800 uppercase">Inventory Shortfall Warning</p>
          </div>
          <ul className="space-y-1">
            {shortfalls.map((shortfall, idx) => (
              <li key={idx} className="text-xs font-bold text-amber-700 flex items-center gap-1">
                <span className="w-1 h-1 bg-amber-500 rounded-full"></span>
                {shortfall.item_name}: Need {shortfall.required} {shortfall.unit}, 
                have {shortfall.available} — restock {shortfall.deficit} {shortfall.unit} before job
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Line Items */}
      <div className="space-y-3">
        <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Line Items</p>
        {lineItems.length === 0 && (
          <div className="text-center py-8 border-2 border-dashed border-slate-100 rounded-2xl text-slate-400 text-sm font-bold">
            Use quick-add above or add a custom line item below
          </div>
        )}
        {lineItems.map((item, index) => (
          <div key={index} className="bg-white border-2 border-slate-100 rounded-2xl p-4 space-y-3">
            <div className="flex gap-2">
              <Input
                placeholder="Description"
                value={item.description}
                onChange={(e) => updateLine(index, 'description', e.target.value)}
                className="flex-1 rounded-xl font-bold border-slate-100 h-11"
              />
              <button onClick={() => removeLine(index)} className="text-red-400 hover:text-red-600 px-2">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            {/* Inventory Requirements Display */}
            {item.service_requirements && item.service_requirements.length > 0 && (
              <div className="bg-purple-50 border border-purple-100 rounded-xl p-3 space-y-2">
                <div className="flex items-center gap-1">
                  <Package className="w-3 h-3 text-purple-600" />
                  <span className="text-[9px] font-black text-purple-700 uppercase">Inventory Required:</span>
                </div>
                {item.service_requirements.map((req, reqIdx) => (
                  <div key={reqIdx} className="flex items-center justify-between text-xs">
                    <span className="font-bold text-slate-700">{req.item_name}</span>
                    <span className="font-black text-purple-600">
                      {req.quantity_required} {req.unit} per service
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[8px] font-black uppercase text-slate-400 ml-1">Unit</label>
                <Input value={item.unit} onChange={(e) => updateLine(index, 'unit', e.target.value)}
                  className="rounded-xl font-bold border-slate-100 h-10 text-sm" />
              </div>
              <div>
                <label className="text-[8px] font-black uppercase text-slate-400 ml-1">Qty</label>
                <Input type="number" value={item.quantity}
                  onChange={(e) => updateLine(index, 'quantity', parseFloat(e.target.value) || 0)}
                  className="rounded-xl font-bold border-slate-100 h-10 text-sm" />
              </div>
              <div>
                <label className="text-[8px] font-black uppercase text-slate-400 ml-1">Unit Price ($)</label>
                <Input type="number" value={item.price}
                  onChange={(e) => updateLine(index, 'price', parseFloat(e.target.value) || 0)}
                  className="rounded-xl font-bold border-slate-100 h-10 text-sm" />
              </div>
            </div>
            <div className="text-right text-xs font-black text-slate-500 uppercase">
              Line Total: <span className="text-slate-900">${(item.quantity * item.price).toFixed(2)}</span>
            </div>
          </div>
        ))}

        <Button onClick={addBlankLine} variant="outline"
          className="w-full rounded-2xl border-dashed border-2 h-12 text-slate-400 font-black uppercase text-[10px] gap-2">
          <Plus className="w-4 h-4" /> Add Custom Line Item
        </Button>
      </div>

      {/* Notes */}
      <div>
        <label className="text-[10px] font-black uppercase text-slate-400 ml-1 mb-1 block">Notes / Scope of Work (Optional)</label>
        <textarea
          className="w-full bg-white border-2 border-slate-100 rounded-2xl p-4 font-bold text-slate-800 text-sm outline-none focus:ring-2 ring-purple-400 resize-none"
          rows={3}
          placeholder="Any special instructions, access details, or scope notes..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      {/* Totals */}
      <div className="bg-slate-950 rounded-[2.5rem] p-8 text-white shadow-2xl">
        <div className="space-y-3 mb-6">
          <div className="flex justify-between text-[10px] font-bold uppercase opacity-60">
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          {config?.gst_registered && (
            <div className="flex justify-between text-[10px] font-bold uppercase text-purple-400">
              <span>GST (10%)</span>
              <span>${gst.toFixed(2)}</span>
            </div>
          )}
          <div className="h-px bg-slate-800 my-3" />
          <div className="flex justify-between items-end">
            <div>
              <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Total</p>
              <p className="text-4xl font-black italic tracking-tighter">${total.toFixed(2)}</p>
            </div>
            <p className="text-[8px] font-bold text-slate-500 mb-1 uppercase">
              {config?.gst_registered ? 'Inc. GST' : 'Ex. GST'}
            </p>
          </div>
        </div>

        <Button
          onClick={saveQuote}
          disabled={loading}
          className="w-full h-14 bg-purple-600 hover:bg-purple-500 text-white rounded-2xl font-black uppercase tracking-widest text-xs flex gap-2"
        >
          {loading ? <Loader2 className="animate-spin w-4 h-4" /> : <Save className="w-4 h-4" />}
          {loading ? 'Saving...' : 'Save Quote'}
        </Button>
      </div>
    </div>
  );
};
